/** Keep names / companies / projects / tech as spoken (same pronunciation, English letters). */

const STOP = new Set([
  'i', 'my', 'me', 'we', 'our', 'you', 'the', 'a', 'an', 'and', 'or', 'in', 'on', 'of',
  'to', 'for', 'with', 'that', 'this', 'it', 'is', 'am', 'are', 'was', 'were', 'have',
  'has', 'had', 'used', 'after', 'from', 'their', 'there', 'then', 'than', 'but', 'not',
  'at', 'as', 'by', 'be', 'been', 'being', 'do', 'did', 'does', 'so', 'if', 'when',
  'who', 'what', 'which', 'into', 'about', 'over', 'also', 'just', 'more', 'last',
  'next', 'new', 'old', 'one', 'two', 'five', 'six', 'years', 'year', 'name',
  'work', 'working', 'company', 'project',
]);

const FILLER = new Set(['the', 'a', 'an', 'my', 'is', 'at', 'in', 'of', 'to', 'from']);

const TECH = [
  'Python', 'React', 'Java', 'JavaScript', 'TypeScript', 'Node', 'Node.js',
  'SQL', 'PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'AWS', 'API', 'SQLAlchemy',
  'Django', 'Flask', 'Spring', 'Spring Boot', 'Next.js', 'HTML', 'CSS', 'Docker',
  'Kubernetes', 'Git', 'GraphQL', 'REST', 'Excel', 'SAP', 'Kafka', 'Nginx',
];

export function parseKeepList(text) {
  return String(text || '')
    .split(/[,;\n]+/)
    .map((item) => item.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

export function expandKeepPhrase(phrase) {
  const raw = String(phrase || '').replace(/\s+/g, ' ').trim();
  if (!raw) return [];
  const out = [raw];
  raw.split(/\s+/).forEach((part) => {
    if (part.length > 2 && !STOP.has(part.toLowerCase())) out.push(part);
  });
  return out;
}

export function keepWordsFromScript(script) {
  const found = [];
  const seen = new Set();
  const add = (word) => {
    expandKeepPhrase(word).forEach((item) => {
      const key = item.toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      found.push(item);
    });
  };
  TECH.forEach((term) => {
    const re = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(script || '')) add(term);
  });
  const src = String(script || '');
  const named = src.match(/\b(?:my name is|i am|i'm)\s+([A-Za-z][A-Za-z.'-]*(?:\s+[A-Za-z][A-Za-z.'-]*){0,3})/i);
  if (named) add(named[1]);
  const company = src.match(/\b(?:work at|working at|company is|from)\s+([A-Za-z][A-Za-z0-9&.-]*(?:\s+[A-Za-z][A-Za-z0-9&.-]*){0,5})/i);
  if (company) add(company[1]);
  const multi = src.match(/\b[A-Z][a-z]+(?:\s+[A-Z][A-Za-z]+)+/g) || [];
  multi.forEach(add);
  const tokens = src.match(/[A-Za-z][A-Za-z0-9.+#-]*/g) || [];
  tokens.forEach((word) => {
    if (STOP.has(word.toLowerCase())) return;
    if (word[0] === word[0].toUpperCase() && word[0] !== word[0].toLowerCase()) add(word);
  });
  return found;
}

export function foldKeep(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/aa/g, 'a')
    .replace(/ee/g, 'i')
    .replace(/oo/g, 'u')
    .replace(/ph/g, 'f')
    .replace(/w/g, 'v')
    .replace(/[^a-z0-9]/g, '');
}

export function phoneticKeep(text) {
  return foldKeep(text)
    .replace(/[aeiou]+/g, (chunk) => chunk[0])
    .replace(/(.)\1+/g, '$1');
}

function levenshtein(a, b) {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const dist = Array.from({ length: rows }, () => new Array(cols).fill(0));
  for (let i = 0; i <= a.length; i += 1) dist[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) dist[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dist[i][j] = Math.min(dist[i - 1][j] + 1, dist[i][j - 1] + 1, dist[i - 1][j - 1] + cost);
    }
  }
  return dist[a.length][b.length];
}

function closeEnough(spoken, keep) {
  if (!spoken || !keep) return false;
  if (spoken === keep) return true;
  const n = Math.max(spoken.length, keep.length);
  const m = Math.min(spoken.length, keep.length);
  if (n < 4) return spoken === keep;
  if (m < n * 0.65) return false;
  const allow = Math.max(1, Math.min(3, Math.floor(n / 3)));
  if (levenshtein(spoken, keep) <= allow) return true;
  const ps = phoneticKeep(spoken);
  const pk = phoneticKeep(keep);
  if (!ps || !pk) return false;
  if (ps === pk) return true;
  const n2 = Math.max(ps.length, pk.length);
  const m2 = Math.min(ps.length, pk.length);
  if (n2 < 4 || m2 < n2 * 0.65) return false;
  return levenshtein(ps, pk) <= Math.max(1, Math.min(2, Math.floor(n2 / 3)));
}

/** Replace near-pronunciations with the canonical keep-list spelling. */
export function snapKeepWords(text, keepText) {
  const keep = parseKeepList(keepText)
    .flatMap(expandKeepPhrase)
    .map((item) => ({ raw: item, fold: foldKeep(item), words: item.split(/\s+/).filter(Boolean) }))
    .filter((item) => item.fold.length >= 3)
    .sort((a, b) => b.fold.length - a.fold.length || b.words.length - a.words.length);
  const src = String(text || '').replace(/\s+/g, ' ').trim();
  if (!src || !keep.length) return src;
  const tokens = src.split(' ');
  const used = new Array(tokens.length).fill(false);
  const out = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (used[i]) continue;
    const firstPlain = tokens[i].replace(/[.,!?]/g, '').toLowerCase();
    if (FILLER.has(firstPlain)) {
      out.push(tokens[i]);
      used[i] = true;
      continue;
    }
    let matched = null;
    let take = 1;
    const maxSpan = Math.min(4, tokens.length - i);
    for (const item of keep) {
      const w = Math.max(1, item.words.length);
      const spans = w === 1
        ? Array.from({ length: Math.min(3, maxSpan) }, (_, k) => Math.min(3, maxSpan) - k)
        : (w <= maxSpan ? [w] : []);
      for (const span of spans) {
        const option = tokens.slice(i, i + span).join(' ');
        if (closeEnough(foldKeep(option), item.fold)) {
          matched = item.raw;
          take = span;
          break;
        }
      }
      if (matched) break;
    }
    if (matched) {
      out.push(matched);
      for (let k = 0; k < take; k += 1) used[i + k] = true;
    } else {
      out.push(tokens[i]);
      used[i] = true;
    }
  }
  return out.join(' ');
}

export function mergeKeepText(...parts) {
  const seen = new Set();
  const out = [];
  parts.flatMap((part) => parseKeepList(part)).forEach((item) => {
    expandKeepPhrase(item).forEach((word) => {
      const key = word.toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(word);
    });
  });
  return out.join(', ');
}
